import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";

const SHOP_MENU = [
  { to: "/shop", label: "Shop all" },
  { to: "/shop?category=Fishing", label: "Fishing" },
  { to: "/shop?category=Hunting", label: "Hunting" },
  { heading: "Apparel" },
  { to: "/shop?type=shirts", label: "Shirts & hoodies" },
  { to: "/shop?type=jackets", label: "Jackets" },
  { to: "/shop?type=pants", label: "Pants & shorts" },
];

export default function Navbar() {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-ink/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-1">
          <span className="font-display font-black text-2xl tracking-tighter">GO</span>
          <span className="font-display font-black text-2xl tracking-tighter text-brand">BIG</span>
          <span className="font-display font-black text-2xl tracking-tighter">FOREVER</span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-muted-foreground ml-2">
            NZ · AU
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          <Link to="/" data-testid="nav-home" className="text-sm font-medium hover:text-brand transition-colors">
            Home
          </Link>
          <div className="relative group" data-testid="nav-shop-group">
            <Link
              to="/shop"
              data-testid="nav-shop"
              className="text-sm font-medium hover:text-brand transition-colors inline-flex items-center gap-1 py-5"
            >
              Shop <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
            </Link>
            <div
              className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-all duration-150 absolute left-1/2 -translate-x-1/2 top-full w-52 bg-white border border-ink/10 shadow-xl rounded-md py-2 z-50"
              data-testid="nav-shop-dropdown"
            >
              {SHOP_MENU.map((item) =>
                item.heading ? (
                  <p key={item.heading} className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-t border-ink/5 mt-1">
                    {item.heading}
                  </p>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-shop-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="block px-4 py-2 text-sm font-medium hover:bg-secondary hover:text-brand transition-colors"
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          </div>
          <Link to="/track" data-testid="nav-track-order" className="text-sm font-medium hover:text-brand transition-colors">
            Track Order
          </Link>
          <Link to="/contact" data-testid="nav-support" className="text-sm font-medium hover:text-brand transition-colors">
            Support
          </Link>
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="nav-search-btn"
            onClick={() => navigate("/shop")}
            className="hover:text-brand transition-colors"
            aria-label="Search"
          >
            <Search size={20} />
          </button>
          <Link to="/cart" data-testid="nav-cart-btn" className="relative hover:text-brand transition-colors">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-2 -right-2 bg-brand text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            className="md:hidden"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-ink/10 bg-white px-5 py-4 flex flex-col gap-3">
          <Link to="/" onClick={() => setOpen(false)} className="text-sm font-medium" data-testid="mobile-nav-home">Home</Link>
          {SHOP_MENU.map((item) =>
            item.heading ? (
              <p key={item.heading} className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground pt-1">{item.heading}</p>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={`text-sm font-medium ${item.to === "/shop" ? "" : "pl-3 text-ink/80"}`}
                data-testid={`mobile-nav-shop-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {item.label}
              </Link>
            )
          )}
          <Link to="/track" onClick={() => setOpen(false)} className="text-sm font-medium" data-testid="mobile-nav-track-order">Track Order</Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="text-sm font-medium" data-testid="mobile-nav-support">Support</Link>
        </nav>
      )}
    </header>
  );
}
